/* global google */
import React, { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onZipCodeChange,
  onStreetNumberChange,
  onAddressValidation, // New callback for address validation
  placeholder = "Enter address",
  className = "",
  id = "address-autocomplete"
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const [addressSelected, setAddressSelected] = useState(false); // Track if address was selected

  // Helper function to check if text contains a zip code
  const hasZipCode = (text) => {
    return /\b\d{5}(?:-\d{4})?\b/.test(text);
  };

  // Helper function to extract address components from text
  const extractAddressComponents = (fullAddress) => {
    let streetNumber = '';
    let zipCode = '';
    
    if (fullAddress) {
      // Try to extract street number (first number in the address)
      const streetNumberMatch = fullAddress.match(/^\d+/);
      if (streetNumberMatch) {
        streetNumber = streetNumberMatch[0];
      }
      
      // Try to extract ZIP code (5 digits, optionally followed by dash and 4 more digits)
      const zipCodeMatch = fullAddress.match(/\b\d{5}(?:-\d{4})?\b/);
      if (zipCodeMatch) {
        zipCode = zipCodeMatch[0];
      }
    }
    
    return { streetNumber, zipCode };
  };

  useEffect(() => {
    const initializeGoogleMaps = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        console.error('Google Maps API key is not configured');
        return;
      }

      try {
        // 2025 Standard: Use the modern script loading with callback
        window.initMap = async () => {
          try {
            // Import the places library using the new 2025 standard method
            await google.maps.importLibrary("places");
            setIsLoaded(true);
          } catch (error) {
            console.error('Error importing places library:', error);
            // Fallback: check if places is available in the old way
            if (window.google?.maps?.places) {
              setIsLoaded(true);
            }
          }
        };

        // Load Google Maps API with the modern approach
        if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=initMap`;
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        } else if (window.google?.maps) {
          // If already loaded, just initialize
          window.initMap();
        }
      } catch (error) {
        console.error('Error loading Google Maps API:', error);
      }
    };

    initializeGoogleMaps();

    // Cleanup
    return () => {
      if (window.initMap) {
        delete window.initMap;
      }
    };
  }, []);

  const handleInputChange = async (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setAddressSelected(false); // Reset selection when user types
    
    // Always extract street number from any input
    const extractedData = extractAddressComponents(inputValue);
    if (onStreetNumberChange) {
      onStreetNumberChange(extractedData.streetNumber);
    }
    
    if (inputValue.length > 2 && isLoaded && window.google?.maps?.places) {
      try {
        // First try the new 2025 Standard API
        try {
          const request = {
            input: inputValue,
            includedRegionCodes: ['us']
          };
          
          console.log('New API request:', request);
          const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          if (suggestions && suggestions.length > 0) {
            console.log('New API suggestions:', suggestions);
            console.log('First suggestion detailed:', JSON.stringify(suggestions[0], null, 2));
            setSuggestions(suggestions.slice(0, 5));
            setShowSuggestions(true);
            setSelectedIndex(-1);
            
            // Address suggestions found, clear any validation errors
            if (onAddressValidation) {
              onAddressValidation(true, hasZipCode(inputValue), addressSelected);
            }
            return;
          }
        } catch (newApiError) {
          console.log('New API failed, trying fallback:', newApiError);
          console.log('Error details:', newApiError.message);
        }

        // Fallback to traditional AutocompleteService
        const service = new google.maps.places.AutocompleteService();
        const request = {
          input: inputValue,
          types: ['geocode'],
          componentRestrictions: { country: 'us' }
        };

        console.log('Old API request:', request);
        service.getPlacePredictions(request, (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            console.log('Old API predictions:', predictions);
            console.log('First prediction detailed:', JSON.stringify(predictions[0], null, 2));
            setSuggestions(predictions.slice(0, 5));
            setShowSuggestions(true);
            setSelectedIndex(-1);
            
            // Address suggestions found, clear any validation errors
            if (onAddressValidation) {
              onAddressValidation(true, hasZipCode(inputValue), addressSelected);
            }
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
            
            // No suggestions found - don't show error immediately, wait for blur
            setSuggestions([]);
            setShowSuggestions(false);
          }
        });

      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      
      // Clear validation when input is too short or empty
      if (onAddressValidation) {
        onAddressValidation(true, false, false); // Reset validation state
      }
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    // Get the place ID from the suggestion
    let placeId = '';
    if (suggestion.placePrediction?.placeId) {
      placeId = suggestion.placePrediction.placeId;
    } else if (suggestion.placePrediction?.place_id) {
      placeId = suggestion.placePrediction.place_id;
    } else if (suggestion.place_id) {
      placeId = suggestion.place_id;
    }

    // Immediately hide dropdown
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    setAddressSelected(true); // Mark that user selected an address

    console.log('Selected suggestion:', suggestion);
    console.log('Place ID:', placeId);

    // If we have a place ID, get detailed place information including ZIP code
    if (placeId && window.google?.maps?.places) {
      try {
        // Use the new 2025 Place API
        const { Place } = await google.maps.importLibrary("places");
        
        const place = new Place({
          id: placeId,
          requestedLanguage: 'en'
        });

        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress']
        });

        console.log('Place details:', place);

        // Extract address components
        let streetNumber = '';
        let streetName = '';
        let city = '';
        let state = '';
        let zipCode = '';
        let fullAddressWithZip = '';
        
        if (place.addressComponents) {
          place.addressComponents.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) {
              streetNumber = component.longText;
            } else if (types.includes('route')) {
              streetName = component.longText;
            } else if (types.includes('locality')) {
              city = component.longText;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.shortText;
            } else if (types.includes('postal_code')) {
              zipCode = component.longText;
            }
          });

          // Build full address with ZIP code
          if (streetNumber && streetName) {
            fullAddressWithZip = `${streetNumber} ${streetName}`;
            if (city) fullAddressWithZip += `, ${city}`;
            if (state) fullAddressWithZip += `, ${state}`;
            if (zipCode) fullAddressWithZip += ` ${zipCode}`;
          } else if (place.formattedAddress) {
            fullAddressWithZip = place.formattedAddress;
          }
        }

        console.log('Extracted components:', { streetNumber, streetName, city, state, zipCode });
        console.log('Full address with ZIP:', fullAddressWithZip);

        // Update the input field with the full address including ZIP code
        if (fullAddressWithZip) {
          onChange(fullAddressWithZip);
        }

        // Pass street number for API use
        if (onStreetNumberChange) {
          onStreetNumberChange(streetNumber);
        }
        
        // Update ZIP code field if callback is provided
        if (onZipCodeChange && zipCode) {
          onZipCodeChange(zipCode);
        }

      } catch (error) {
        console.error('Error getting place details:', error);
        // Fallback: use the original suggestion text
        let fallbackText = '';
        if (suggestion.placePrediction?.text?.text) {
          fallbackText = suggestion.placePrediction.text.text;
        } else if (suggestion.description) {
          fallbackText = suggestion.description;
        }
        
        if (fallbackText) {
          onChange(fallbackText);
          
          // Try to extract data from fallback text
          const extractedData = extractAddressComponents(fallbackText);
          if (onStreetNumberChange) {
            onStreetNumberChange(extractedData.streetNumber);
          }
          if (onZipCodeChange && extractedData.zipCode) {
            onZipCodeChange(extractedData.zipCode);
          }
        }
      }
    } else {
      // Fallback: use suggestion text and try to extract components
      let fallbackText = '';
      if (suggestion.placePrediction?.text?.text) {
        fallbackText = suggestion.placePrediction.text.text;
      } else if (suggestion.description) {
        fallbackText = suggestion.description;
      }
      
      if (fallbackText) {
        onChange(fallbackText);
        
        const extractedData = extractAddressComponents(fallbackText);
        if (onStreetNumberChange) {
          onStreetNumberChange(extractedData.streetNumber);
        }
        if (onZipCodeChange && extractedData.zipCode) {
          onZipCodeChange(extractedData.zipCode);
        }
      }
    }
  };

  const getSuggestionText = (suggestion) => {
    console.log('Processing AutocompleteSuggestion:', suggestion);
    
    let main = '';
    let secondary = '';
    let key = '';
    let fullAddress = '';
    
    // Handle AutocompleteSuggestion API format
    if (suggestion.placePrediction) {
      console.log('Processing placePrediction format');
      
      // Get place ID
      key = suggestion.placePrediction.placeId || '';
      
      // Try to get the full address text
      if (suggestion.placePrediction.text?.text) {
        fullAddress = suggestion.placePrediction.text.text;
      } else if (suggestion.placePrediction.description) {
        fullAddress = suggestion.placePrediction.description;
      }
      
      // If we have structured format, use it
      if (suggestion.placePrediction.structuredFormat?.mainText?.text) {
        main = suggestion.placePrediction.structuredFormat.mainText.text;
        if (suggestion.placePrediction.structuredFormat?.secondaryText?.text) {
          secondary = suggestion.placePrediction.structuredFormat.secondaryText.text;
        }
      }
    } else if (suggestion.queryPrediction) {
      console.log('Processing queryPrediction format');
      
      // Handle query predictions (for broader geocoding results)
      if (suggestion.queryPrediction.text?.text) {
        fullAddress = suggestion.queryPrediction.text.text;
      }
      
      if (suggestion.queryPrediction.structuredFormat?.mainText?.text) {
        main = suggestion.queryPrediction.structuredFormat.mainText.text;
        if (suggestion.queryPrediction.structuredFormat?.secondaryText?.text) {
          secondary = suggestion.queryPrediction.structuredFormat.secondaryText.text;
        }
      }
    } else {
      console.log('Unknown suggestion format, using fallback parsing');
      // Fallback for any other format
      fullAddress = suggestion.description || suggestion.text || '';
      key = suggestion.place_id || '';
    }
    
    console.log('Extracted data:', { main, secondary, fullAddress, key });
    
    // If we have structured main and secondary, use them
    if (main && secondary) {
      console.log('Using structured format');
      return {
        main: main,
        secondary: secondary,
        key: key || Math.random().toString()
      };
    }
    
    // Otherwise, parse the full address to ensure ZIP is visible
    if (fullAddress) {
      console.log('Parsing full address:', fullAddress);
      const addressParts = fullAddress.split(',').map(part => part.trim());
      if (addressParts.length >= 3) {
        // Format: Street, City, State ZIP
        main = addressParts[0];
        secondary = addressParts.slice(1).join(', ');
      } else if (addressParts.length === 2) {
        // Format: Street, City State ZIP
        main = addressParts[0];
        secondary = addressParts[1];
      } else {
        // Single part - show as main
        main = fullAddress;
        secondary = '';
      }
      
      console.log('Parsed result:', { main, secondary });
      return {
        main: main,
        secondary: secondary,
        key: key || Math.random().toString()
      };
    }
    
    // Final fallback
    console.log('Using fallback');
    return {
      main: main || 'Address',
      secondary: secondary || '',
      key: key || Math.random().toString()
    };
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    // Don't hide if clicking within the suggestions dropdown
    if (e.relatedTarget && e.relatedTarget.closest('.address-suggestions-dropdown')) {
      return;
    }
    
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
      
      // Validate on blur if we have a value and it wasn't selected from suggestions
      if (value && value.length > 2 && !addressSelected && onAddressValidation) {
        const hasZipCodeInValue = hasZipCode(value);
        onAddressValidation(false, hasZipCodeInValue, addressSelected);
      }
    }, 150);
  };

  return (
    <div className={`address-autocomplete-container ${className}`}>
      <div className="address-input-wrapper">
        {value && (
          <button
            type="button"
            className="clear-address-btn"
            onClick={() => {
              onChange("");
              setAddressSelected(false);
              if (onStreetNumberChange) onStreetNumberChange("");
              if (onZipCodeChange) onZipCodeChange("");
            }}
            aria-label="Clear address"
          >
            <svg className="clear-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        )}
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className="address-autocomplete-input"
          autoComplete="off"
        />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="address-suggestions-dropdown"
        >
          {suggestions.map((suggestion, index) => {
            const { main, secondary, key } = getSuggestionText(suggestion);
            return (
              <div
                key={key}
                className={`address-suggestion-item ${
                  index === selectedIndex ? 'selected' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur
                  handleSuggestionClick(suggestion);
                }}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="suggestion-main">
                  {main}
                </div>
                {secondary && (
                  <div className="suggestion-secondary">
                    {secondary}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {!isLoaded && (
        <div className="loading-indicator">
          Loading address suggestions...
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete; 